import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import Button from '@adsk/alloy-react-button';
import { optionalPropType } from '@adsk/alloy-react-helpers';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import type { DatePickerDesignTokens } from '@adsk/alloy-react-styles';
import { stylePropType, StyleProp } from '@adsk/alloy-react-theme';

import Cell from './Cell';
import YearMonthDropdown from './YearMonthDropdown';
import {
  getCalendarNavigationProps,
  getDateVariant,
  isForwardDisabled,
  isBackwardDisabled,
} from '../helpers';
import useDateSelection from '../hooks/useDateSelection';
import useNavigation from '../hooks/useNavigation';
import useRangeHover from '../hooks/useRangeHover';
import * as types from '../types';

type UseRangeHover = ReturnType<typeof useRangeHover>;

type CalendarProps = Pick<
  types.DatePickerAllArgs,
  | 'startDate'
  | 'endDate'
  | 'minDate'
  | 'maxDate'
  | 'hasEventForDate'
  | 'tooltipContentForDate'
> &
  Pick<
    ReturnType<typeof useNavigation>,
    'setMonth' | 'setYear' | 'prevMonth' | 'nextMonth' | 'updateActiveMonths'
  > &
  Pick<ReturnType<typeof useDateSelection>, 'onDateSelected'> &
  types.CalendarProps & {
    calendars: types.CalendarType[];
    calendar: types.CalendarType;
    onDateHover?: UseRangeHover[1];
    stateHoveredDate?: UseRangeHover[0];
    range?: boolean;
    shouldShowSelection?: boolean;
  };

const isCalendarDate = (
  day: types.DateObj | types.BlankCalendarDay | null | undefined,
): day is types.DateObj =>
  typeof day === 'object' && day !== null && 'date' in day;

const weaveColumnSlotStyle = (
  cellWidth: number | undefined,
  cellHeight: number | undefined,
  calendarTokens: DatePickerDesignTokens['calendar'] | undefined,
  options?: { transparentBorder?: boolean },
) => {
  const slotStyle = {
    width: cellWidth,
    height: cellHeight,
    flexShrink: 0,
    display: 'flex',
    boxSizing: 'content-box' as const,
  };

  if (!calendarTokens?.cellBorder) {
    return {
      ...slotStyle,
      ...calendarTokens?.cellWrapper,
    };
  }

  return {
    ...slotStyle,
    ...calendarTokens.cellWrapper,
    ...calendarTokens.cellBorder,
    ...(options?.transparentBorder
      ? {
          borderRightColor: 'transparent',
          borderBottomColor: 'transparent',
        }
      : undefined),
  };
};

const renderLeftIcon: RenderIconType = (p) => <ChevronLeftIcon {...p} />;
const renderRightIcon: RenderIconType = (p) => <ChevronRightIcon {...p} />;

const renderDateCell = ({
  dateObj,
  key,
  range,
  startDate,
  endDate,
  stateHoveredDate,
  shouldShowSelection,
  hasEventForDate,
  tooltipContentForDate,
  onDateSelected,
  updateActiveMonths,
  onDateHover,
  cellStyle,
  skipEmpty,
}: {
  dateObj: types.DateObj | types.BlankCalendarDay | null;
  key: string;
  range?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  stateHoveredDate?: Date | null;
  shouldShowSelection?: boolean;
  hasEventForDate?: CalendarProps['hasEventForDate'];
  tooltipContentForDate?: CalendarProps['tooltipContentForDate'];
  onDateSelected: CalendarProps['onDateSelected'];
  updateActiveMonths: CalendarProps['updateActiveMonths'];
  onDateHover?: CalendarProps['onDateHover'];
  cellStyle?: StyleProp;
  skipEmpty?: boolean;
}) => {
  if (!dateObj || !isCalendarDate(dateObj)) {
    if (skipEmpty) {
      return null;
    }
    return <Cell key={key} variant={Cell.VARIANTS.SPACER} />;
  }

  let selectedDates = [startDate, endDate];

  if (startDate && endDate && startDate > endDate) {
    selectedDates = [endDate, startDate];
  }

  const currentVariant: types.CellVariant = getDateVariant({
    range,
    selectedDates,
    date: dateObj.date,
    stateHoveredDate,
    isOutside: dateObj.isOutside,
    shouldShowSelection,
  });

  return (
    <Cell
      key={key}
      data-testid={`data-cell-${currentVariant}`}
      today={dateObj.today}
      selected={dateObj.selected && shouldShowSelection}
      disabled={!dateObj.selectable}
      hasEvent={!!hasEventForDate?.({ ...dateObj })}
      tooltipContent={
        tooltipContentForDate ? tooltipContentForDate({ ...dateObj }) : null
      }
      onClick={(event) => {
        onDateSelected(dateObj, updateActiveMonths);
        event.currentTarget.blur();
      }}
      variant={currentVariant}
      onMouseEnter={() => onDateHover?.(dateObj.date)}
      style={cellStyle}
    >
      {dateObj.date.getDate()}
    </Cell>
  );
};

const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      className,
      style,
      calendar,
      calendars,
      minDate,
      maxDate,
      onDateHover,
      onDateSelected,
      updateActiveMonths,
      hasEventForDate,
      tooltipContentForDate,
      stateHoveredDate,
      prevMonth,
      nextMonth,
      setMonth,
      setYear,
      startDate,
      endDate,
      range,
      shouldShowSelection,
      ...rest
    },
    ref,
  ) => {
    const {
      selectedMonth,
      selectedYear,
      weekdays,
      weeks,
      months,
      years,
      year,
      month,
      index,
    } = calendar;

    const { hideBack, hideForward } = getCalendarNavigationProps({
      calendarKey: index,
      totalCalendars: calendars.length,
    });

    const theme = useTheme();
    const t = theme.tokens?.component?.datePicker;
    const arrowButtonStyles = t?.calendar?.navButton;
    const navIsAbsolute = arrowButtonStyles?.position === 'absolute';
    const useWeekGrid = !!t?.calendar?.weeks;

    const weekdayLabels = useMemo(
      () =>
        weekdays.map((weekday) => (
          <div
            key={`${month}/${year}-${weekday}`}
            css={[
              useWeekGrid
                ? [
                    weaveColumnSlotStyle(t?.cellWidth, undefined, t?.calendar, {
                      transparentBorder: true,
                    }),
                    {
                      height: 'auto',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTopWidth: 0,
                      borderLeftWidth: 0,
                    },
                  ]
                : {
                    width: t?.cellWidth ?? t?.cellHeight,
                  },
              { textAlign: 'center' },
            ]}
          >
            {weekday}
          </div>
        )),
      [
        weekdays,
        month,
        year,
        t?.cellWidth,
        t?.cellHeight,
        t?.calendar,
        useWeekGrid,
      ],
    );

    const weekdayCells = useMemo(
      () =>
        weekdays.map((weekday) => (
          <Cell
            key={`${month}/${year}-${weekday}`}
            variant={Cell.VARIANTS.SPACER}
          >
            {weekday}
          </Cell>
        )),
      [weekdays, month, year],
    );

    const weaveWeekRows = useMemo(
      () =>
        weeks.map((week, weekIndex) => (
          <div
            key={`${month}/${year}-week-${weekIndex}`}
            css={t?.calendar?.weekRow}
          >
            {week.map((dateObj, dayIndex) => {
              const cellKey = `${month}/${year}-${dayIndex}/${weekIndex}`;

              return (
                <div
                  key={cellKey}
                  css={weaveColumnSlotStyle(
                    t?.cellWidth,
                    t?.cellHeight,
                    t?.calendar,
                  )}
                >
                  {isCalendarDate(dateObj)
                    ? renderDateCell({
                        range,
                        startDate,
                        endDate,
                        stateHoveredDate,
                        shouldShowSelection,
                        hasEventForDate,
                        tooltipContentForDate,
                        onDateSelected,
                        updateActiveMonths,
                        onDateHover,
                        dateObj,
                        key: cellKey,
                        skipEmpty: true,
                        cellStyle: {
                          width: '100%',
                          height: '100%',
                          flex: 1,
                          minWidth: 0,
                        },
                      })
                    : null}
                </div>
              );
            })}
          </div>
        )),
      [
        weeks,
        t?.calendar,
        t?.cellWidth,
        t?.cellHeight,
        range,
        startDate,
        endDate,
        stateHoveredDate,
        shouldShowSelection,
        hasEventForDate,
        tooltipContentForDate,
        onDateSelected,
        updateActiveMonths,
        onDateHover,
        month,
        year,
      ],
    );

    const dateCells = useMemo(() => {
      if (useWeekGrid) {
        return null;
      }

      return weeks.flatMap((week, weekIndex) =>
        week.map((dateObj, dayIndex) =>
          renderDateCell({
            range,
            startDate,
            endDate,
            stateHoveredDate,
            shouldShowSelection,
            hasEventForDate,
            tooltipContentForDate,
            onDateSelected,
            updateActiveMonths,
            onDateHover,
            dateObj: dateObj || null,
            key: `${month}/${year}-${dayIndex}/${weekIndex}`,
          }),
        ),
      );
    }, [
      useWeekGrid,
      hasEventForDate,
      onDateSelected,
      tooltipContentForDate,
      updateActiveMonths,
      weeks,
      onDateHover,
      endDate,
      month,
      range,
      startDate,
      stateHoveredDate,
      year,
      shouldShowSelection,
    ]);

    return (
      <div
        {...rest}
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            flexDirection: 'column',
            width: `calc(100% / ${calendars.length})`,
            paddingLeft: t?.calendar?._?.paddingLeft,
            paddingRight: t?.calendar?._?.paddingRight,
            paddingBottom: t?.calendar?._?.paddingBottom,
            paddingTop: t?.calendar?._?.paddingTop,
          },
          style,
        ]}
      >
        <div
          css={[
            t?.calendar?.header,
            {
              display: 'flex',
              alignItems: 'center',
            },
          ]}
        >
          {!hideBack && (
            <Button
              renderIcon={renderLeftIcon}
              variant={Button.VARIANTS.TERTIARY}
              style={[
                {
                  ...arrowButtonStyles,
                  ...(navIsAbsolute ? { left: 0 } : {}),
                },
              ]}
              onClick={prevMonth}
              disabled={isBackwardDisabled({ calendars, minDate })}
              data-testid="DatePickerArrowBack"
            />
          )}
          <div css={{ display: 'flex' }}>
            <YearMonthDropdown
              items={months}
              selectedItem={selectedMonth}
              onSelectedItemChange={({ selectedItem }) => {
                if (selectedItem) {
                  setMonth(calendar.index, selectedItem.key);
                }
              }}
              disabled={!months || months.length <= 1}
              data-testid={`DatePickerCalendar-${calendar.index}-month`}
            >
              {selectedMonth?.label}
            </YearMonthDropdown>
            <YearMonthDropdown
              items={years}
              selectedItem={selectedYear}
              style={{
                marginLeft: t?.yearMonthDropdown?._?.marginLeft,
              }}
              onSelectedItemChange={({ selectedItem }) => {
                if (selectedItem) {
                  setYear(calendar.index, selectedItem.key);
                }
              }}
              disabled={!years || years.length <= 1}
              data-testid={`DatePickerCalendar-${calendar.index}-year`}
            >
              {selectedYear?.label}
            </YearMonthDropdown>
          </div>
          {!hideForward && (
            <Button
              renderIcon={renderRightIcon}
              variant={Button.VARIANTS.TERTIARY}
              style={[
                {
                  ...arrowButtonStyles,
                  ...(navIsAbsolute ? { right: 0 } : {}),
                },
              ]}
              onClick={nextMonth}
              disabled={isForwardDisabled({ calendars, maxDate })}
              data-testid="DatePickerArrowForward"
            />
          )}
        </div>
        {useWeekGrid ? (
          <>
            <div css={t?.calendar?.weekHeader}>{weekdayLabels}</div>
            <div key={`${month}${year}`} css={t?.calendar?.weeks}>
              {weaveWeekRows}
            </div>
          </>
        ) : (
          <div
            key={`${month}${year}`}
            css={[
              {
                display: 'flex',
                flexWrap: 'wrap',
                borderTopWidth: t?.calendar?.grid?.borderTopWidth,
                borderTopStyle: t?.calendar?.grid?.borderTopStyle,
                borderTopColor: t?.calendar?.grid?.borderTopColor,
                borderLeftWidth: t?.calendar?.grid?.borderLeftWidth,
                borderLeftStyle: t?.calendar?.grid?.borderLeftStyle,
                borderLeftColor: t?.calendar?.grid?.borderLeftColor,
              },
            ]}
          >
            {weekdayCells}
            {dateCells}
          </div>
        )}
      </div>
    );
  },
);

Calendar.displayName = 'Calendar';

const monthPropType = PropTypes.any;

Calendar.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Current calendar data */
  calendar: PropTypes.shape({
    index: PropTypes.number.isRequired,
    firstDayOfMonth: PropTypes.instanceOf(Date).isRequired,
    lastDayOfMonth: PropTypes.instanceOf(Date).isRequired,
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    weeks: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
    ).isRequired,
    months: PropTypes.arrayOf(monthPropType.isRequired).isRequired,
    weekdays: PropTypes.arrayOf(PropTypes.any).isRequired,
    years: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.number.isRequired,
        label: PropTypes.number.isRequired,
      }).isRequired,
    ).isRequired,
    selectedMonth: optionalPropType(monthPropType),
    selectedYear: optionalPropType(
      PropTypes.shape({
        key: PropTypes.number.isRequired,
        label: PropTypes.number.isRequired,
      }),
    ),
  }).isRequired,
  /** all of the calendars */
  calendars: PropTypes.array.isRequired,
  /** on Date hover callback */
  onDateHover: PropTypes.func,
  /** current date hovered */
  stateHoveredDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Flag whether the date picker is for a date range of a single value */
  range: PropTypes.bool,
  onDateSelected: PropTypes.func.isRequired,
  updateActiveMonths: PropTypes.func.isRequired,
  hasEventForDate: PropTypes.func,
  tooltipContentForDate: PropTypes.func,
  prevMonth: PropTypes.func.isRequired,
  nextMonth: PropTypes.func.isRequired,
  setMonth: PropTypes.func.isRequired,
  setYear: PropTypes.func.isRequired,
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
};

export default Calendar;
